import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import NEW_API from "../../../Api/NewApi";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import {
  updateProject,
  saveProjectInterlocutor,
  deleteProjectInterlocutor,
} from "../../../REDUX/actions/appProjects";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";

const TEXT_FIELDS = [
  { name: "firstName", label: "First name" },
  { name: "lastName", label: "Last name" },
  { name: "email", label: "Email" },
  { name: "phoneNumber", label: "Phone number" },
];

/**
 * Slide 7 — interlocutors are contact records: first name, last name, email,
 * phone, gender and a note. Added two ways: an existing prospect account of
 * the store (copied, `user` keeps the link) or typed freely. The first one is
 * created automatically from the prospect chosen at creation; clicking a card
 * transfers the "Contact Principal Projet" role.
 */
const ProjectInterlocutors = ({ project }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { updateLoading } = useSelector((state) => state.appProjectsReducer);
  // null = closed, {} = new, {id,...} = editing
  const [form, setForm] = useState(null);
  // Prospect picker: null = closed, else the chosen option (or {} = none yet).
  const [picked, setPicked] = useState(null);
  const [prospects, setProspects] = useState([]);

  // The store's prospect / client accounts (scoped server-side).
  useEffect(() => {
    const controller = new AbortController();
    NEW_API.get("/api/customers", {
      params: { page: 1, pageSize: 1000 },
      signal: controller.signal,
    })
      .then(({ data }) => setProspects(data?.data ?? []))
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    return () => controller.abort();
  }, []);

  const genderOptions = [
    { name: t("Male"), code: "male" },
    { name: t("Female"), code: "female" },
  ];

  const interlocutors = project.interlocutors ?? [];
  const mainId = project.mainInterlocutor?.id;

  // Accounts already on the project aren't offered again.
  const linkedIds = interlocutors.map((item) => item.user?.id).filter(Boolean);
  const prospectOptions = prospects
    .filter((account) => !linkedIds.includes(account.id))
    .map((account) => ({
      name: [
        `${account.firstName ?? ""} ${account.lastName ?? ""}`.trim() ||
          account.username,
        account.email,
      ]
        .filter(Boolean)
        .join(" — "),
      code: account.id,
      account,
    }));

  const setMain = (id) => {
    if (id === mainId) return;
    dispatch(updateProject(project.id, { mainInterlocutor: id }, t));
  };

  const handleAddProspect = () => {
    const { account } = picked;
    dispatch(
      saveProjectInterlocutor(
        {
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email,
          phoneNumber: account.phoneNumber,
          user: account.id,
        },
        project.id,
        t,
        () => setPicked(null)
      )
    );
  };

  const handleSave = () => {
    // `user` stays as it is: an edit never relinks the account.
    const { gender, user, ...rest } = form;
    dispatch(
      saveProjectInterlocutor(
        { ...rest, gender: gender?.code ?? null },
        project.id,
        t,
        () => setForm(null)
      )
    );
  };

  return (
    <div className="flex-1 d-flex flex-column">
      <div className="modalContent px-5 pt-4 pb-5">
        <div className="row g-3">
          {interlocutors.map((interlocutor) => {
            const isMain = interlocutor.id === mainId;
            return (
              <div className="col-6" key={interlocutor.id}>
                <div
                  className={`p-3 px-4 br10 pointer h-100 position-relative ${
                    isMain ? "text-white" : "b-1-lightGrey"
                  }`}
                  style={{ backgroundColor: isMain ? "#345d68" : "#fff" }}
                  onClick={() => setMain(interlocutor.id)}
                >
                  <span className="d-block sBold f16">
                    {interlocutor.gender
                      ? `${t(
                          interlocutor.gender === "male" ? "Male" : "Female"
                        )} — `
                      : ""}
                    {`${interlocutor.firstName ?? ""} ${
                      interlocutor.lastName ?? ""
                    }`.trim() || "-"}
                  </span>
                  <span className="d-block f13">
                    {interlocutor.email ?? ""}
                  </span>
                  <span className="d-block f13">
                    {interlocutor.phoneNumber ?? ""}
                  </span>
                  {interlocutor.note ? (
                    <span className="d-block f13 fst-italic mt-1">
                      {interlocutor.note}
                    </span>
                  ) : null}
                  {isMain && (
                    <span className="d-inline-block mt-2 me-2 px-2 py-1 br5 f12 sBold bg-white text-blue">
                      {t("Main project contact")}
                    </span>
                  )}
                  {interlocutor.user && (
                    <span
                      className={`d-inline-block mt-2 px-2 py-1 br5 f12 sBold ${
                        isMain ? "bg-white text-blue" : "bg-dimGrey text-blue"
                      }`}
                    >
                      {t("Prospect account")}
                    </span>
                  )}
                  <div
                    className="position-absolute top-0 end-0 m-2 d-flex gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`border-0 bg-transparent f14 ${
                        isMain ? "text-white" : "text-brightGrey"
                      }`}
                      onClick={() =>
                        setForm({
                          ...interlocutor,
                          gender: genderOptions.find(
                            (option) => option.code === interlocutor.gender
                          ),
                        })
                      }
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      type="button"
                      className={`border-0 bg-transparent f14 ${
                        isMain ? "text-white" : "text-red-600"
                      }`}
                      onClick={() =>
                        dispatch(
                          openCenteredModal(
                            <ConfirmModal
                              id={interlocutor.id}
                              label={t("Interlocutor")}
                              onConfirm={() =>
                                dispatch(
                                  deleteProjectInterlocutor(
                                    interlocutor.id,
                                    project.id,
                                    t
                                  )
                                )
                              }
                            />
                          )
                        )
                      }
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {form ? (
          <div className="p-4 bg-dimGrey br10 mt-4">
            <p className="text-uppercase text-dark mb-3">
              {t(form.id ? "Edit interlocutor" : "Add an interlocutor")}
            </p>
            <div className="row g-3">
              {TEXT_FIELDS.map(({ name, label }) => (
                <div className="col-6" key={name}>
                  <label className="f16 text-softBlack sBold mb-1">
                    {t(label)}
                  </label>
                  <TextInput
                    placeholder={t(label)}
                    value={form[name] ?? ""}
                    callback={(e) =>
                      setForm((prev) => ({ ...prev, [name]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div className="col-6">
                <label className="f16 text-softBlack sBold mb-1">
                  {t("Gender")}
                </label>
                <Select
                  placeholder={t("Gender")}
                  options={genderOptions}
                  value={form.gender ?? null}
                  showClear
                  resetable
                  appendToSelf
                  callback={(e) =>
                    setForm((prev) => ({ ...prev, gender: e.value ?? null }))
                  }
                />
              </div>
              <div className="col-12">
                <label className="f16 text-softBlack sBold mb-1">
                  {t("Note")}
                </label>
                <TextArea
                  placeholder={t("Note")}
                  rows={3}
                  value={form.note ?? ""}
                  callback={(e) =>
                    setForm((prev) => ({ ...prev, note: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-3 mt-3">
              <Button variant="outline" onClick={() => setForm(null)}>
                {t("Cancel")}
              </Button>
              <Button
                loading={updateLoading}
                disabled={!form.firstName && !form.lastName}
                onClick={handleSave}
              >
                {t("Save")}
              </Button>
            </div>
          </div>
        ) : picked ? (
          <div className="p-4 bg-dimGrey br10 mt-4">
            <p className="text-uppercase text-dark mb-3">
              {t("Add a prospect")}
            </p>
            <Select
              filter
              appendToSelf
              placeholder={t("Choose a prospect")}
              options={prospectOptions}
              value={picked.code ? picked : null}
              callback={(e) => setPicked(e.value ?? {})}
            />
            <div className="d-flex justify-content-end gap-3 mt-3">
              <Button variant="outline" onClick={() => setPicked(null)}>
                {t("Cancel")}
              </Button>
              <Button
                loading={updateLoading}
                disabled={!picked.code}
                onClick={handleAddProspect}
              >
                {t("Save")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 d-flex gap-3">
            <Button onClick={() => setPicked({})}>{t("Add a prospect")}</Button>
            <Button variant="outline" onClick={() => setForm({})}>
              {t("Add an interlocutor")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectInterlocutors;
